import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Tooltip,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  PhotoCamera as PhotoCameraIcon,
  Note as NoteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { vehicleConditionAssessmentService, vehicleService, subClientService } from '../services/api';
import { VehicleConditionAssessment, Vehicle, SubClient } from '../types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AssessmentViewModal from '../components/FieldAgent/AssessmentViewModal';
import { useLocation } from 'react-router-dom';

const ConditionAssessments: React.FC = () => {
  const [assessments, setAssessments] = useState<VehicleConditionAssessment[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [subClients, setSubClients] = useState<SubClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<VehicleConditionAssessment | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const location = useLocation();
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState<number | ''>('');
  const [subClientFilter, setSubClientFilter] = useState<number | ''>('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'depart' | 'retour'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'good' | 'fair' | 'poor' | 'critical'>('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadVehicles();
    loadSubClients();
  }, []);

  useEffect(() => {
    loadAssessments();
  }, [page, rowsPerPage, vehicleFilter, subClientFilter, typeFilter, statusFilter, dateFromFilter, dateToFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subClientIdParam = params.get('subClientId');
    if (subClientIdParam) {
      const parsedId = parseInt(subClientIdParam, 10);
      if (!isNaN(parsedId)) {
        setSubClientFilter(parsedId);
      }
    }
  }, [location.search]);

  const loadVehicles = async () => {
    try {
      const response = await vehicleService.getAll();
      setVehicles(response.data.data);
    } catch (err: any) {
      console.error('Error loading vehicles:', err);
    }
  };

  const loadSubClients = async () => {
    try {
      const response = await subClientService.getAll();
      setSubClients(response.data.data || []);
    } catch (err: any) {
      console.error('Error loading sub-clients:', err);
    }
  };

  const loadAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        per_page: rowsPerPage,
        page: page + 1,
      };
      
      if (vehicleFilter) {
        params.vehicle_id = vehicleFilter;
      }

      if (subClientFilter) {
        params.sub_client_id = subClientFilter;
      }
      
      if (typeFilter !== 'all') {
        params.assessment_type = typeFilter;
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (dateFromFilter) {
        params.date_from = dateFromFilter;
      }
      
      if (dateToFilter) {
        params.date_to = dateToFilter;
      }
      
      const response = await vehicleConditionAssessmentService.getAllAssessments(params);
      
      if (response.data.data) {
        setAssessments(response.data.data);
        setTotalCount(response.data.total || response.data.data.length);
      } else {
        setAssessments(response.data);
        setTotalCount(response.data.length);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (assessment: VehicleConditionAssessment) => {
    setSelectedAssessment(assessment);
    setViewModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return '#4caf50';
      case 'fair': return '#ff9800';
      case 'poor': return '#f44336';
      case 'critical': return '#d32f2f';
      default: return '#9e9e9e';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'good': return 'Bon';
      case 'fair': return 'Correct';
      case 'poor': return 'Mauvais';
      case 'critical': return 'Critique';
      default: return 'Inconnu';
    }
  };

  const getTypeLabel = (type: string | null | undefined) => {
    if (!type) return 'N/A';
    switch (type) {
      case 'depart': return 'Départ';
      case 'retour': return 'Retour';
      default: return type;
    }
  };

  // Filter assessments by search query
  const filteredAssessments = assessments.filter(assessment => {
    const query = searchQuery.toLowerCase();
    const vehiclePlate = assessment.vehicle?.registration_number?.toLowerCase() || '';
    const vehicleMake = assessment.vehicle?.marque?.toLowerCase() || '';
    const vehicleModel = assessment.vehicle?.modele?.toLowerCase() || '';
    const subClientName = assessment.sub_client?.name?.toLowerCase() || '';
    const subClientEmail = assessment.sub_client?.email?.toLowerCase() || '';
    
    return (
      vehiclePlate.includes(query) ||
      vehicleMake.includes(query) ||
      vehicleModel.includes(query) ||
      subClientName.includes(query) ||
      subClientEmail.includes(query)
    );
  });

  // Statistics
  const totalAssessments = assessments.length;
  const departCount = assessments.filter(a => a.assessment_type === 'depart').length;
  const retourCount = assessments.filter(a => a.assessment_type === 'retour').length;
  const goodCount = assessments.filter(a => a.status === 'good').length;
  const criticalCount = assessments.filter(a => a.status === 'critical').length;

  if (loading && assessments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          États des Lieux
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: 1, minWidth: '200px' }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total
            </Typography>
            <Typography variant="h4">
              {totalAssessments}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: '200px' }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Départ
            </Typography>
            <Typography variant="h4">
              {departCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: '200px' }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Retour
            </Typography>
            <Typography variant="h4">
              {retourCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: '200px' }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Bon État
            </Typography>
            <Typography variant="h4" sx={{ color: '#4caf50' }}>
              {goodCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: '200px' }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Critique
            </Typography>
            <Typography variant="h4" sx={{ color: '#d32f2f' }}>
              {criticalCount}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: '200px' }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Véhicule</InputLabel>
              <Select
                value={vehicleFilter}
                onChange={(e) => setVehicleFilter(e.target.value as number | '')}
                label="Véhicule"
              >
                <MenuItem value="">Tous</MenuItem>
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.registration_number} - {vehicle.marque} {vehicle.modele}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Client</InputLabel>
              <Select
                value={subClientFilter}
                onChange={(e) => setSubClientFilter(e.target.value as number | '')}
                label="Client"
              >
                <MenuItem value="">Tous</MenuItem>
                {subClients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.company_name || client.name} ({client.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as 'all' | 'depart' | 'retour')}
                label="Type"
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="depart">Départ</MenuItem>
                <MenuItem value="retour">Retour</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'good' | 'fair' | 'poor' | 'critical')}
                label="Statut"
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="good">Bon</MenuItem>
                <MenuItem value="fair">Correct</MenuItem>
                <MenuItem value="poor">Mauvais</MenuItem>
                <MenuItem value="critical">Critique</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="Date de début"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              type="date"
              label="Date de fin"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setVehicleFilter('');
                setSubClientFilter('');
                setTypeFilter('all');
                setStatusFilter('all');
                setDateFromFilter('');
                setDateToFilter('');
                setSearchQuery('');
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Assessments Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Véhicule</TableCell>
                <TableCell>Sous-client</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Photos</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Créé par</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssessments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PhotoCameraIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Aucun état des lieux trouvé
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchQuery || vehicleFilter || typeFilter !== 'all' || statusFilter !== 'all'
                          ? 'Aucun résultat pour votre recherche'
                          : 'Aucun état des lieux enregistré'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAssessments.map((assessment) => (
                  <TableRow key={assessment.id} hover>
                    <TableCell>
                      {assessment.assessment_date
                        ? format(new Date(assessment.assessment_date), 'dd MMM yyyy', { locale: fr })
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(assessment.assessment_type)}
                        size="small"
                        color={assessment.assessment_type === 'depart' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      {assessment.vehicle ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CarIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {assessment.vehicle.registration_number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {assessment.vehicle.marque} {assessment.vehicle.modele}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {assessment.sub_client ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2">
                              {assessment.sub_client.display_name || assessment.sub_client.name}
                            </Typography>
                            {assessment.sub_client.email && (
                              <Typography variant="caption" color="text.secondary">
                                {assessment.sub_client.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(assessment.status)}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(assessment.status),
                          color: 'white',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhotoCameraIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {assessment.photos?.length || assessment.photo_count || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NoteIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {assessment.notes?.length || assessment.note_count || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {assessment.created_by_name || assessment.created_by_user?.name || 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Voir">
                        <IconButton
                          size="small"
                          onClick={() => handleView(assessment)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Card>

      {/* View Modal */}
      {selectedAssessment && (
        <AssessmentViewModal
          assessment={selectedAssessment}
          assessments={assessments}
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedAssessment(null);
          }}
        />
      )}
    </Box>
  );
};

export default ConditionAssessments;

